import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Clock, ArrowUpRight } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = ["Ristorante / Pizzeria", "Hotel / Resort", "B&B / Domus", "Bottega / Negozio", "Altro"];
const BUDGETS = ["Fino a €1.500", "€1.500 — €3.000", "€3.000 — €5.000", "Oltre €5.000", "Da definire"];

const EMPTY = {
  name: "",
  business: "",
  business_type: TYPES[0],
  email: "",
  phone: "",
  budget: BUDGETS[4],
  message: "",
};

const inputCls =
  "w-full rounded-xl border border-line bg-ink px-4 py-3.5 text-sm text-bone placeholder:text-faded transition-colors duration-300 focus:border-terracotta focus:outline-none";

function InfoRow({ icon: Icon, children, testid }) {
  return (
    <div className="flex items-center gap-4" data-testid={testid}>
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-line bg-panel text-terracotta">
        <Icon size={17} strokeWidth={1.8} />
      </span>
      <span className="text-sm text-stone">{children}</span>
    </div>
  );
}

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Richiesta inviata! Ti ricontattiamo entro 24 ore.", {
        description: "Grazie da Nex Studio Design.",
      });
      setForm(EMPTY);
    } catch (err) {
      toast.error("Invio non riuscito. Riprova tra un istante.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contatti" className="py-24 lg:py-32">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-5 sm:px-8 lg:grid-cols-5 lg:gap-14">
        <div className="lg:col-span-2">
          <SectionHeading
            tag="Contatti"
            title={
              <>
                Parliamo del tuo <em className="italic text-terracotta">locale.</em>
              </>
            }
            sub="Raccontaci la tua attività: ti rispondiamo entro 24 ore con un'analisi gratuita della tua presenza online e un primo orientamento di prezzo."
          />

          <Reveal delay={0.2}>
            <div className="mt-10 space-y-4">
              <InfoRow icon={Mail} testid="contact-info-email">
                info@nexstudiodesign.it
              </InfoRow>
              <InfoRow icon={Phone} testid="contact-info-phone">
                +39 352 233 7690
              </InfoRow>
              <InfoRow icon={MapPin} testid="contact-info-locations">
                Venezia · Mestre · Veneto
              </InfoRow>
              <InfoRow icon={Clock} testid="contact-info-hours">
                Lun — Ven, 9:00 — 18:00
              </InfoRow>
            </div>
          </Reveal>

          <Reveal delay={0.3}>
            <div className="glass mt-10 rounded-2xl p-5">
              <p className="font-accent text-[11px] uppercase tracking-[0.24em] text-ochre">
                Audit gratuito
              </p>
              <p className="mt-2 text-sm leading-relaxed text-stone">
                Per ogni nuova richiesta analizziamo gratis sito e scheda Google attuali: scopri
                subito cosa perdi rispetto ai competitor.
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="lg:col-span-3">
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="rounded-3xl border border-line bg-panel p-6 sm:p-8 lg:p-9"
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="f-name" className="mb-2 block font-accent text-[10px] uppercase tracking-[0.22em] text-stone">
                  Nome e cognome *
                </label>
                <input
                  id="f-name"
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Maria Bianchi"
                  data-testid="contact-input-name"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="f-business" className="mb-2 block font-accent text-[10px] uppercase tracking-[0.22em] text-stone">
                  Nome dell'attività *
                </label>
                <input
                  id="f-business"
                  required
                  value={form.business}
                  onChange={set("business")}
                  placeholder="Trattoria dai Nostri"
                  data-testid="contact-input-business"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="f-type" className="mb-2 block font-accent text-[10px] uppercase tracking-[0.22em] text-stone">
                  Tipologia
                </label>
                <select
                  id="f-type"
                  value={form.business_type}
                  onChange={set("business_type")}
                  data-testid="contact-select-type"
                  className={`${inputCls} appearance-none`}
                >
                  {TYPES.map((t) => (
                    <option key={t} value={t} className="bg-ink">
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="f-email" className="mb-2 block font-accent text-[10px] uppercase tracking-[0.22em] text-stone">
                  Email *
                </label>
                <input
                  id="f-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  placeholder="maria@trattoria.it"
                  data-testid="contact-input-email"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="f-phone" className="mb-2 block font-accent text-[10px] uppercase tracking-[0.22em] text-stone">
                  Telefono
                </label>
                <input
                  id="f-phone"
                  type="tel"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="+39 333 000 0000"
                  data-testid="contact-input-phone"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="f-budget" className="mb-2 block font-accent text-[10px] uppercase tracking-[0.22em] text-stone">
                  Budget orientativo
                </label>
                <select
                  id="f-budget"
                  value={form.budget}
                  onChange={set("budget")}
                  data-testid="contact-select-budget"
                  className={`${inputCls} appearance-none`}
                >
                  {BUDGETS.map((b) => (
                    <option key={b} value={b} className="bg-ink">
                      {b}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="f-message" className="mb-2 block font-accent text-[10px] uppercase tracking-[0.22em] text-stone">
                  Messaggio
                </label>
                <textarea
                  id="f-message"
                  rows={4}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Raccontaci il tuo locale, cosa ti serve e quando vorresti partire…"
                  data-testid="contact-input-message"
                  className={`${inputCls} resize-none`}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="group mt-6 inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-terracotta px-7 py-4 font-accent text-xs font-semibold uppercase tracking-[0.18em] text-[#1c0d08] transition-colors duration-300 hover:bg-[#f0695a] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {sending ? "Invio in corso…" : "Invia la richiesta"}
              {!sending && (
                <ArrowUpRight
                  size={16}
                  className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                />
              )}
            </button>
            <p className="mt-3 text-center text-xs text-faded">
              Risposta garantita entro 24 ore lavorative. Nessuno spam, mai.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}